// Void-from-mobile adapter — converts a void request pushed by the legacy Java
// app (VoidItemsActivity.toMap) into the SAME JobAction.VOID jobcard the cashier
// produces from pos.tableInstance.ts:voidItems. Once added to the jobchain via
// usePosJobsStore().add(job, true), the existing plugins remove the items, print
// the void slips (thermalprinter2.plugin → printJobToKitchen VOID branch) and
// record the void to DynamoDB (job.to.voidItem.plugin) — no extra work here.
//
// # void request (from the phone)
// {
//   "by": "<employeeName>",
//   "type": "table",
//   "uuid": <tableNumber:int>,
//   "reason": "<text>",
//   "mapVoidUniqueId": { "<lineUniqueId>": <qtyToVoid:number>, ... }
// }
//
// mapVoidUniqueId keys are cart-line uniqueids, which equal TableItem.uuid on the
// computed table (TableHelper.calculateItems → uuid: item.uniqueid). The VOID
// json2 is keyed by that same uuid with a negative quantity, which is what
// JobchainV1-1.voidItem uses to reduce line.quantity.

import { JobAction, JobMode } from "~/interface/Job";
import JobBuilder from "./JobBuilderV1";

export interface MobileVoidResult {
  job: any | null;
  matchedCount: number;
  requestedCount: number;
}

export default class AdapterMobileVoidV1 {
  // `stableKey` (the RTDB child key of the pushed void node) is identical across
  // every tab, so deriving the jobId from it makes two tabs produce the SAME
  // jobId — the kitchen void slip's UniqueId then dedupes downstream (PrintVoid)
  // and the jobchain collapses to one node. Mirrors AdapterMobileV1 using the
  // cloud datetime. Falls back to a per-call timestamp when absent.
  fromCloudToJobchain(voidString: string, stableKey?: string): MobileVoidResult {
    const payload = JSON.parse(voidString);

    // The void payload (VoidItemsActivity.toMap) carries only the table NUMBER in
    // `uuid` — no `alias`. Resolve the table alias from the number.
    const alias = usePosMobileV1Store().convertUuidToTable(payload.uuid);

    const map: Record<string, number> = payload.mapVoidUniqueId || {};
    const requestedCount = Object.keys(map).length;

    if (!alias) {
      return { job: null, matchedCount: 0, requestedCount };
    }

    const tableItems: any[] = usePosJobsStore().tables[alias]?.items || [];

    const json2: Record<string, number> = {};
    const items: any[] = [];

    for (const [uniqueId, rawQty] of Object.entries(map)) {
      const qty = Math.abs(Number(rawQty) || 0);
      if (qty <= 0) continue;

      const item = tableItems.find((i: any) => i.uuid === uniqueId);
      if (!item) continue;

      // Don't void more than what's on the table.
      const voidQty = Math.min(qty, Number(item.quantity) || qty);
      json2[uniqueId] = voidQty * -1;
      items.push(item);
    }

    const matchedCount = items.length;
    if (matchedCount === 0) {
      return { job: null, matchedCount: 0, requestedCount };
    }

    const nowIso = useServerTime().nowISO();
    const job = new JobBuilder()
      .withTableAlias(alias)
      .withTableUuid(alias)
      .withActionType(JobAction.VOID)
      .withMode(JobMode.TABLE)
      .withJson2(json2)
      .withSource("m")
      .withEmployee({
        name: payload.by || "mobile",
        id: "mobile",
      })
      .withPrint(true)
      // Keep the real timestamp; make the jobId deterministic across tabs.
      .withJobIdAndTimestamp(nowIso)
      .withJobId(stableKey || nowIso)
      .withCustom({
        __items: items,
        __reason: payload.reason || "",
      })
      .build();

    return { job, matchedCount, requestedCount };
  }
}
